import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * JigsawPuzzleResponsiveWithSound.jsx
 *
 * - Fully responsive square board (clamp width), square tiles
 * - Desktop: HTML5 drag & drop
 * - Mobile: tap-to-place fallback
 * - Sounds (free): drop and success (Google Actions sound library)
 *
 * Notes:
 * - Replace SOUND_* URLs with your own if you prefer other sounds.
 * - For best UX on mobile, user should interact with the page once (touch/click)
 *   to allow audio playback without browser blocking.
 */

export default function JigsawPuzzleResponsiveWithSound() {
  const puzzleImages = [
    "/images/avatars/rama.png",
    "/images/avatars/krishna.png",
    "/images/avatars/narasimha.png",
    "/images/avatars/parashurama.png",
    "/images/avatars/kurma.png",
    "/images/avatars/varaha.png",
    "/images/avatars/vamana.png",
    "/images/avatars/matsya.png",

  ];

  // Free/public sound assets (Google Actions library)
  const SOUND_DROP =
    "https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg";
  const SOUND_SUCCESS =
    "https://actions.google.com/sounds/v1/cartoon/clang_and_wobble.ogg";

  const [selectedImage, setSelectedImage] = useState(puzzleImages[0]);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [pieces, setPieces] = useState([]);
  const [board, setBoard] = useState([]);
  const [moves, setMoves] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [selectedPieceForTap, setSelectedPieceForTap] = useState(null);

  const timerRef = useRef(null);
  const dropAudioRef = useRef(null);
  const successAudioRef = useRef(null);

  // preload audio
  useEffect(() => {
    try {
      dropAudioRef.current = new Audio(SOUND_DROP);
      dropAudioRef.current.volume = 0.6;
      successAudioRef.current = new Audio(SOUND_SUCCESS);
      successAudioRef.current.volume = 0.9;
      // don't auto-play; just load
      dropAudioRef.current.load();
      successAudioRef.current.load();
    } catch (e) {
      // ignore audio init errors
      dropAudioRef.current = null;
      successAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (startedAt) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }, 500);
    }
    return () => clearInterval(timerRef.current);
  }, [startedAt]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const initPuzzle = useCallback(
    (r = rows, c = cols) => {
      const total = r * c;
      const list = Array.from({ length: total }, (_, i) => ({ id: i }));
      const shuffled = shuffle([...list]);
      setPieces(shuffled);
      setBoard(Array(total).fill(null));
      setMoves(0);
      setStartedAt(null);
      setElapsed(0);
      setGameCompleted(false);
      setShowNotification(false);
      setSelectedPieceForTap(null);
    },
    [rows, cols]
  );

  useEffect(() => {
    initPuzzle(rows, cols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols, selectedImage]);

  function shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // compute tile background styles
  const pieceStyle = useMemo(() => {
    const styles = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        const posX = cols > 1 ? (c * 100) / (cols - 1) : 50;
        const posY = rows > 1 ? (r * 100) / (rows - 1) : 50;
        styles[idx] = {
          backgroundImage: `url(${selectedImage})`,
          backgroundSize: `${cols * 100}% ${rows * 100}%`,
          backgroundPosition: `${posX}% ${posY}%`,
          backgroundRepeat: "no-repeat",
          width: "100%",
          height: "100%",
        };
      }
    }
    return styles;
  }, [rows, cols, selectedImage]);

  // play drop sound safely
  const playDropSound = () => {
    const a = dropAudioRef.current;
    if (!a) return;
    // modern browsers may block play until user interaction
    a.currentTime = 0;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  };

  const playSuccessSound = () => {
    const a = successAudioRef.current;
    if (!a) return;
    a.currentTime = 0;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  };

  // unified placement logic
  const placePieceOnBoard = (pieceId, from, targetIndex) => {
    if (!startedAt) setStartedAt(Date.now());

    setBoard((prev) => {
      const next = [...prev];
      const existing = next[targetIndex];

      // remove piece from origin
      if (from === "tray") {
        setPieces((prevPieces) => prevPieces.filter((p) => p.id !== pieceId));
      } else if (typeof from === "number") {
        next[from] = null;
      }

      // handle occupied target
      if (existing !== null && existing !== undefined) {
        if (typeof from === "number") {
          // swap with origin
          next[from] = existing;
        } else {
          // send existing back to tray
          setPieces((prevPieces) => [...prevPieces, { id: existing }]);
        }
      }

      next[targetIndex] = pieceId;
      return next;
    });

    setMoves((m) => m + 1);
    playDropSound();
    setTimeout(() => {
      // check win after state settles
      checkWin();
    }, 120);

    setSelectedPieceForTap(null);
  };

  // drag handlers
  const handleDragStart = (e, pieceId, from) => {
    try {
      e.dataTransfer.setData("text/plain", JSON.stringify({ pieceId, from }));
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // ignore (mobile browsers)
    }
  };

  const handleDropToBoard = (e, targetIndex) => {
    e.preventDefault();
    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }
    const { pieceId, from } = data;
    placePieceOnBoard(pieceId, from, targetIndex);
  };

  const handleDropToTray = (e) => {
    e.preventDefault();
    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }
    const { pieceId, from } = data;
    if (from !== "tray" && typeof from === "number") {
      setBoard((prev) => {
        const next = [...prev];
        next[from] = null;
        return next;
      });
      setPieces((prev) => [...prev, { id: pieceId }]);
      setMoves((m) => m + 1);
      playDropSound();
    }
  };

  // tap handlers (mobile)
  const onTapPiece = (pieceId) => {
    setSelectedPieceForTap((prev) => (prev === pieceId ? null : pieceId));
  };

  const onTapBoardCell = (idx) => {
    if (selectedPieceForTap !== null && pieces.some((p) => p.id === selectedPieceForTap)) {
      // place selected tray piece
      placePieceOnBoard(selectedPieceForTap, "tray", idx);
      return;
    }
    // select board piece to move
    const pid = board[idx];
    if (pid !== null && pid !== undefined) {
      setSelectedPieceForTap(pid);
    }
  };

  // check win
  const checkWin = () => {
    const total = rows * cols;
    for (let i = 0; i < total; i++) {
      if (board[i] !== i) return false;
    }
    clearInterval(timerRef.current);
    setGameCompleted(true);
    setShowNotification(true);
    playSuccessSound();
    return true;
  };

  useEffect(() => {
    // if board changes, update placed count and detect solved
    const total = rows * cols;
    const placed = board.filter((b) => b !== null && b !== undefined).length;
    if (placed === total) {
      // small delay then check
      setTimeout(() => {
        checkWin();
      }, 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  useEffect(() => {
    if (showNotification) {
      const t = setTimeout(() => setShowNotification(false), 4500);
      return () => clearTimeout(t);
    }
  }, [showNotification]);

  // derived
  const total = rows * cols;
  const placedCount = board.filter((b) => b !== null && b !== undefined).length;

  // responsive board sizing:
  // use CSS clamp via inline style for container width; CSS aspect-ratio keeps it square.
  const boardSizeStyle = {
    width: "clamp(260px, 90vw, 720px)",
    aspectRatio: "1 / 1",
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl md:text-4xl font-bold text-center mb-2 text-red-600">Jigsaw Puzzle</h1>
      <p className="text-center text-sm md:text-base text-gray-600 mb-4">Square tiles • Fully responsive • Drag & Drop (desktop) • Tap-to-place (mobile)</p>

      {/* Stats & Controls */}
      <div className="flex flex-wrap justify-center gap-3 mb-4">
        <div className="px-3 py-1 bg-yellow-100 rounded-full flex items-center gap-2">
          <strong className="text-red-600">Moves</strong>
          <span>{moves}</span>
        </div>
        <div className="px-3 py-1 bg-yellow-100 rounded-full flex items-center gap-2">
          <strong className="text-red-600">Time</strong>
          <span>{formatTime(elapsed)}</span>
        </div>

        <div className="px-3 py-1 bg-blue-50 rounded-full flex items-center gap-2">
          <strong className="text-blue-600">Difficulty</strong>
          {[3, 4, 5].map((lvl) => (
            <button
              key={lvl}
              onClick={() => {
                setRows(lvl);
                setCols(lvl);
              }}
              className={`ml-2 px-3 py-1 rounded-full text-sm ${rows === lvl ? "bg-blue-600 text-white" : "bg-white text-blue-600 border border-blue-100"}`}
            >
              {lvl}×{lvl}
            </button>
          ))}
        </div>

        <div className="px-3 py-1 bg-white rounded-full flex items-center gap-2">
          <label className="text-sm font-medium mr-2">Image</label>
          <div className="flex gap-2">
            {puzzleImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setSelectedImage(img)}
                className={`w-10 h-10 rounded overflow-hidden border-2 ${selectedImage === img ? "border-red-500 ring-2 ring-red-200" : "border-gray-200"}`}
              >
                <img src={img} alt={`img-${i}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* notification */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            className="fixed top-4 right-4 z-50 bg-green-500 text-white rounded-lg px-4 py-3 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <div className="text-2xl">🎉</div>
              <div>
                <div className="font-semibold">Puzzle Completed!</div>
                <div className="text-sm">Solved in {moves} moves — {formatTime(elapsed)}</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-center gap-6">
        {/* Board */}
        <div className="bg-white rounded-xl p-3 shadow-lg">
          <div className="text-center font-semibold text-gray-700 mb-2">Puzzle Board</div>
          <div style={boardSizeStyle} className="relative rounded overflow-hidden bg-gray-100" onDragOver={(e) => e.preventDefault()}>
            <div
              className="w-full h-full grid"
              style={{
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                gap: 2,
              }}
            >
              {Array.from({ length: total }).map((_, idx) => {
                const pieceId = board[idx];
                const isSelected = selectedPieceForTap !== null && selectedPieceForTap === pieceId;
                return (
                  <div
                    key={idx}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDropToBoard(e, idx)}
                    onClick={() => onTapBoardCell(idx)}
                    className={`relative border border-gray-200 bg-white flex items-center justify-center ${isSelected ? "ring-2 ring-blue-300" : ""}`}
                    style={{ minWidth: 0, minHeight: 0 }}
                  >
                    {pieceId !== null && pieceId !== undefined ? (
                      <motion.div
                        layout
                        draggable
                        onDragStart={(e) => handleDragStart(e, pieceId, idx)}
                        initial={{ scale: 0.98, opacity: 0.96 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="w-full h-full"
                        style={{ ...pieceStyle[pieceId] }}
                      />
                    ) : (
                      <div className="text-xs text-gray-300 pointer-events-none">Drop</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center mt-3 text-sm text-gray-600">Placed: {placedCount}/{total}</div>
        </div>

        {/* Tray */}
        <div className="bg-white rounded-xl p-3 shadow-lg w-full md:w-96">
          <div className="text-center font-semibold text-gray-700 mb-2">Puzzle Pieces (Tray)</div>
          <div onDragOver={(e) => e.preventDefault()} onDrop={handleDropToTray} className="min-h-[200px] border-2 border-dashed border-gray-200 rounded p-2 flex flex-wrap gap-2 content-start">
            {pieces.length === 0 ? (
              <div className="w-full text-center py-8 text-sm text-gray-400">All pieces placed — tap a board tile to select/move</div>
            ) : (
              pieces.map((p) => {
                const isSelected = selectedPieceForTap === p.id;
                return (
                  <motion.button
                    key={p.id}
                    onClick={() => onTapPiece(p.id)}
                    onDragStart={(e) => handleDragStart(e, p.id, "tray")}
                    draggable
                    layout
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-20 h-20 border rounded overflow-hidden shadow-sm relative ${isSelected ? "ring-2 ring-blue-300" : ""}`}
                    style={{ padding: 0 }}
                    title={`Piece ${p.id}`}
                  >
                    <div style={{ ...pieceStyle[p.id], width: "100%", height: "100%" }} />
                  </motion.button>
                );
              })
            )}
          </div>

          <div className="mt-3 flex gap-3 justify-center">
            <button onClick={() => initPuzzle(rows, cols)} className="bg-red-600 text-white px-4 py-2 rounded-full font-semibold shadow">Shuffle / Reset</button>
            <button onClick={() => {
              const all = Array.from({ length: total }, (_, i) => ({ id: i }));
              setPieces(all);
              setBoard(Array(total).fill(null));
              setMoves(0);
              setStartedAt(null);
              setElapsed(0);
              setGameCompleted(false);
              setSelectedPieceForTap(null);
            }} className="bg-gray-100 px-4 py-2 rounded-full font-medium">Clear Board</button>
          </div>

          <div className="mt-4 text-center text-sm text-gray-600">
            Tip: On mobile, tap a tray tile to select, then tap a board cell to place. Tap a board tile to move it.
          </div>
        </div>
      </div>

      {/* Reference image */}
      <div className="mt-6 bg-white rounded-xl p-4 shadow-md max-w-xl mx-auto text-center">
        <h3 className="font-semibold text-gray-700 mb-2">Reference Image</h3>
        <img src={selectedImage} alt="reference" className="w-full h-auto rounded object-contain" />
      </div>

      {/* completion modal */}
      <AnimatePresence>
        {gameCompleted && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-black/40 absolute inset-0" onClick={() => setGameCompleted(false)} />
            <motion.div className="relative bg-white rounded-xl p-6 shadow-2xl z-50 w-[min(92vw,520px)] text-center">
              <h2 className="text-2xl font-bold mb-2 text-green-600">🎉 Puzzle Completed!</h2>
              <p className="mb-2">You solved it in <strong>{moves}</strong> moves</p>
              <p className="mb-4">Time: <strong>{formatTime(elapsed)}</strong></p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => { initPuzzle(rows, cols); setGameCompleted(false); setShowNotification(false); }} className="bg-green-600 text-white px-4 py-2 rounded-full font-semibold">Play Again</button>
                <button onClick={() => setGameCompleted(false)} className="bg-gray-100 px-4 py-2 rounded-full">Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* instructions */}
      <div className="mt-6 mx-auto max-w-2xl bg-yellow-50 rounded p-4 text-sm text-gray-700">
        <strong>How to play:</strong>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Drag pieces from the tray to the board (desktop), or tap pieces then tap board cells (mobile).</li>
          <li>Arrange all tiles into their correct positions to recreate the full image.</li>
          <li>Use difficulty buttons for 3×3, 4×4, or 5×5.</li>
        </ul>
      </div>
    </div>
  );
}
